import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase-config";
import { collection, getDocs, query, doc, getDoc, where, deleteDoc, addDoc, updateDoc, setDoc, Timestamp, orderBy } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./AdminDashboard.css";
import { jsPDF } from "jspdf";

const autoTable = require('jspdf-autotable').default;

// Fonction pour formater les informations complètes de l'appareil
function formatDeviceInfo(device) {
  if (!device) return "Non précisé";
  
  // Si c'est une chaîne de caractères (User Agent complet)
  if (typeof device === 'string') {
    return device;
  }
  
  // Si c'est un objet avec un userAgent
  if (device.userAgent) {
    return device.userAgent;
  }
  
  // Sinon, formater depuis les propriétés de l'objet
  const parts = [];
  
  if (device.os) {
    parts.push(device.os);
  } else if (device.platform) {
    parts.push(device.platform);
  }
  
  if (device.browser) {
    parts.push(device.browser);
  }
  
  if (device.browserVersion) {
    parts.push(`v${device.browserVersion}`);
  }
  
  if (device.type) {
    parts.push(`(${device.type})`);
  }
  
  return parts.length > 0 ? parts.join(' ') : "Inconnu";
}

// Colonnes à utiliser pour l'affichage, le CSV et le PDF
const ADMIN_COLUMNS = [
  { key: 'name', label: 'Nom' },
  { key: 'date', label: 'Date' },
  { key: 'startTime', label: 'Heure Début' },
  { key: 'endTime', label: 'Heure Fin' },
  { key: 'workTime', label: 'Temps Travail' },
  { key: 'pauseTime', label: 'Temps Pause' },
  { key: 'sites', label: 'Sites autorisés' },
  { key: 'gpsStart', label: 'GPS Début' },
  { key: 'gpsEnd', label: 'GPS Fin' },
  { key: 'zoneStart', label: 'Zone Début' },
  { key: 'zoneEnd', label: 'Zone Fin' },
  { key: 'zonePause', label: 'Zone(s) Pause(s)' },
  { key: 'device', label: '📱 Appareil' },
  { key: 'info', label: 'Info' },
];

function AdminDashboard() {
  const [workSessions, setWorkSessions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesHistory, setSitesHistory] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  // Mois en cours par défaut (format YYYY-MM)
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [newAbsenceRequests, setNewAbsenceRequests] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // Session manuelle
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState({});
  const siteCache = {};
  // Valeur par défaut pour le temps de session max (en heures)
  const [maxSessionTime, setMaxSessionTime] = useState(12); // 12h par défaut

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Bandeau de validation de feuille de temps
  const timesheetId = searchParams.get('timesheetId');
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [timesheetData, setTimesheetData] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [employerComment, setEmployerComment] = useState('');

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getTimestampInMs = (timestamp) => {
    if (!timestamp) return 0;
    if (typeof timestamp === 'number') {
      // Timestamp en secondes ou millisecondes
      return timestamp > 1e12 ? timestamp : timestamp * 1000;
    }
    if (timestamp.seconds !== undefined) {
      // Firestore Timestamp
      return timestamp.seconds * 1000;
    }
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().getTime();
    }
    return 0;
  };

  const getAuthorizedSitesAtDate = (employeeEmail, timestamp) => {
    if (!timestamp) {
      console.log('🔍 DEBUG - Pas de timestamp fourni');
      return [];
    }

    // Convertir le timestamp en Date pour la comparaison
    const eventTimeMs = getTimestampInMs(timestamp);
    const eventDate = new Date(eventTimeMs);
    console.log('🔍 DEBUG - Date de l\'événement:', eventDate.toLocaleString('fr-FR'));
    
    // Pour Clément TEST uniquement
    const isClementTest = employeeEmail === "bonjour@bonjour.com";
    
    // Historique complet pour Clément
    if (isClementTest) {
      console.log('🔍 DEBUG - Vérification pour Clément TEST');
      console.log('🔍 DEBUG - Sites disponibles:', sites.map(s => ({ id: s.id, nom: s.nom })));
      console.log('🔍 DEBUG - Historique complet:', sitesHistory.filter(h => h.employeeEmail === "bonjour@bonjour.com"));
    }

    // Trouver la dernière modification AVANT la création de la session ou de la pause
    const relevantHistory = sitesHistory
      .filter(history => {
        const hasEmail = history.employeeEmail?.trim() === employeeEmail?.trim();
        const hasChangeDate = history.changeDate != null;
        const changeDateMs = hasChangeDate ? getTimestampInMs(history.changeDate) : 0;
        if (isClementTest) {
          console.log('🔍 DEBUG - Vérification entrée historique:', {
            email: history.employeeEmail,
            hasEmail,
            hasChangeDate,
            changeDate: hasChangeDate ? new Date(changeDateMs).toLocaleString('fr-FR') : null,
            dateModification: changeDateMs,
            datePointage: eventTimeMs,
            estAvantPointage: changeDateMs < eventTimeMs
          });
        }
        return hasEmail && hasChangeDate;
      })
      .map(history => ({
        ...history,
        changeDateObj: new Date(getTimestampInMs(history.changeDate))
      }))
      .filter(history => {
        // Ne prendre que les modifications faites avant l'événement (session ou pause)
        const isBeforeSession = history.changeDateObj.getTime() < eventTimeMs;
        if (isClementTest) {
          console.log('🔍 DEBUG - Comparaison dates:', {
            changeDate: history.changeDateObj.toLocaleString('fr-FR'),
            eventDate: eventDate.toLocaleString('fr-FR'),
            isBeforeSession
          });
        }
        return isBeforeSession;
      })
      .sort((a, b) => b.changeDateObj - a.changeDateObj);

    if (isClementTest) {
      console.log('🔍 DEBUG - Historique pertinent trouvé:', relevantHistory);
    }

    if (relevantHistory.length > 0) {
      const siteIds = relevantHistory[0].newSites;
      
      if (isClementTest) {
        console.log('🔍 DEBUG - Sites autorisés à', eventDate.toLocaleString('fr-FR'), ':', 
          siteIds.map(id => sites.find(s => s.id === id)?.nom || id)
        );
      }
      
      return siteIds;
    } else {
      // Si pas d'historique antérieur à la session, utiliser les sites de l'employé
      const emp = employees.find(e => e.email === employeeEmail);
      const currentSites = emp ? (Array.isArray(emp.siteDeTravail) ? emp.siteDeTravail : [emp.siteDeTravail]) : [];
      
      if (isClementTest) {
        console.log('🔍 DEBUG - Aucun historique antérieur trouvé, utilisation des sites initiaux:', 
          currentSites.map(id => sites.find(s => s.id === id)?.nom || id)
        );
      }
      
      return currentSites;
    }
  };

  // Utilitaire pour fallback sur lat/lng si latitude/longitude absents
  function getLat(location) {
    return location?.latitude !== undefined ? location.latitude : location?.lat;
  }
  function getLng(location) {
    return location?.longitude !== undefined ? location.longitude : location?.lng;
  }

  const findPositionSite = (location) => {
    if (!location || !location.latitude || !location.longitude) {
      return null;
    }
    
    // Chercher parmi tous les sites existants
    const foundSite = sites.find(site => {
      if (!site.latitude || !site.longitude || !site.rayon) {
        return false;
      }
      
      const dist = calculateDistance(site.latitude, site.longitude, location.latitude, location.longitude);
      
      return dist <= site.rayon;
    });

    return foundSite;
  };

  const checkZone = (location, employeeEmail, timestamp, sessionSiteId) => {
    if (!location || !location.latitude || !location.longitude || !timestamp) {
      return false;
    }

    const isClementTest = employeeEmail === "bonjour@bonjour.com";
    
    // Obtenir les sites autorisés à la date donnée
    const authorizedSites = getAuthorizedSitesAtDate(employeeEmail, timestamp);
    
    // Log uniquement pour Clément TEST
    if (isClementTest) {
      console.log(`🔍 DEBUG - Sites autorisés pour le pointage:`, {
        sitesAutorisés: authorizedSites.map(id => sites.find(s => s.id === id)?.nom || id)
      });
    }

    // Trouver le site où l'employé se trouve physiquement
    const actualSite = findPositionSite(location);
    
    if (isClementTest) {
      console.log(`🔍 DEBUG - Site détecté par position:`, actualSite?.nom || "Aucun");
    }

    // Si l'employé est dans un site autorisé, le pointage est valide
    if (actualSite && authorizedSites.includes(actualSite.id)) {
      if (isClementTest) {
        console.log(`🔍 DEBUG - Position validée:`, {
          sitePhysique: actualSite.nom,
          estAutorisé: true
        });
      }
      return true;
    }

    if (isClementTest) {
      console.log(`🔍 DEBUG - Position non validée:`, {
        sitePhysique: actualSite?.nom || "Hors zone",
        sitesAutorisés: authorizedSites.map(id => sites.find(s => s.id === id)?.nom || id)
      });
    }
    
    return false;
  };

  const formatGPSCoordinate = (location, coordinate) => {
    if (!location) return "Non disponible";
    if (coordinate === "latitude") return getLat(location) ?? "Non disponible";
    if (coordinate === "longitude") return getLng(location) ?? "Non disponible";
    return "Non disponible";
  };

  const fetchWorkSessions = async (monthFilter = selectedMonth) => {
    try {
      console.log("Fetching work sessions for month:", monthFilter);
      
      // Parser le mois sélectionné (format YYYY-MM)
      const [year, month] = monthFilter.split('-').map(Number);
      
      // Calculer le début et la fin du mois sélectionné
      const startOfMonth = new Date(year, month - 1, 1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      const endOfMonth = new Date(year, month, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      
      console.log('📅 Chargement des sessions du mois:', {
        mois: monthFilter,
        debut: startOfMonth.toLocaleDateString('fr-FR'),
        fin: endOfMonth.toLocaleDateString('fr-FR')
      });
      
      // Récupérer UNIQUEMENT les sessions du mois sélectionné
      const sessionsSnapshot = await getDocs(
        query(
          collection(db, "workSessions"),
          where("startTime", ">=", Timestamp.fromDate(startOfMonth)),
          where("startTime", "<=", Timestamp.fromDate(endOfMonth)),
          orderBy("startTime", "desc")
        )
      );
      const sessionDocs = sessionsSnapshot.docs;
      
      console.log(`✅ ${sessionDocs.length} sessions chargées pour ${monthFilter}`);

      // Récupérer toutes les pauses en une seule requête
      const breaksSnapshot = await getDocs(collection(db, "breaks"));
      const allBreaks = breaksSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });

      // Organiser les pauses par sessionId
      const breaksBySession = allBreaks.reduce((acc, breakItem) => {
        if (!acc[breakItem.sessionId]) {
          acc[breakItem.sessionId] = [];
        }
        acc[breakItem.sessionId].push(breakItem);
        return acc;
      }, {});

      // Combiner les sessions avec leurs pauses respectives
      const sessionsData = sessionDocs.map(doc => {
        const sessionData = doc.data();
        const sessionBreaks = breaksBySession[doc.id] || [];
        return {
          id: doc.id,
          ...sessionData,
          breaks: sessionBreaks
        };
      });

      // Trier les sessions par date de début (la plus récente en premier)
      const sortedSessions = sessionsData.sort((a, b) => {
        const getStartTime = (session) => {
          if (session.offlineStartTime) {
            return new Date(session.offlineStartTime).getTime();
          }
          if (session.startTime) {
            return session.startTime.toDate().getTime();
          }
          if (session.transmissionStartTime) {
            return session.transmissionStartTime.toDate().getTime();
          }
          return 0;
        };

        return getStartTime(b) - getStartTime(a);
      });

      console.log("Work sessions loaded:", sortedSessions.length);
      setWorkSessions(sortedSessions);
    } catch (error) {
      console.error("Error fetching work sessions:", error);
      setError("Erreur lors du chargement des sessions de travail");
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("AdminDashboard mounted");
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log("Auth state changed:", user ? "logged in" : "logged out");
      if (user) {
        try {
          setLoading(true);
          setError(null);
          
          // Exécuter toutes les requêtes en parallèle
          await Promise.all([
            fetchEmployees(),
            fetchSites(),
            fetchSitesHistory(),
            fetchWorkSessions()
          ]);
          
          setLoading(false);
        } catch (err) {
          console.error("Error loading data:", err);
          setError("Erreur lors du chargement des données");
          setLoading(false);
        }
      } else {
        console.log("Redirecting to login");
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // UseEffect pour recharger les sessions quand le mois sélectionné change
  useEffect(() => {
    if (!loading && employees.length > 0 && sites.length > 0) {
      console.log('🔄 Rechargement des sessions pour le mois:', selectedMonth);
      fetchWorkSessions(selectedMonth);
    }
  }, [selectedMonth]); // Se déclenche uniquement quand selectedMonth change

  // UseEffect pour gérer le mode validation de feuille de temps
  useEffect(() => {
    const loadTimesheetForValidation = async () => {
      if (!timesheetId) {
        setShowValidationBanner(false);
        return;
      }

      try {
        console.log('📋 Chargement de la feuille de temps:', timesheetId);
        
        // Charger la feuille depuis Firebase
        const timesheetDoc = await getDoc(doc(db, 'workSessionsMonth', timesheetId));
        
        if (!timesheetDoc.exists()) {
          console.error('❌ Feuille de temps introuvable');
          return;
        }

        const data = timesheetDoc.data();
        setTimesheetData({ id: timesheetDoc.id, ...data });
        setEmployerComment(data.employerComment || '');
        
        // Appliquer les filtres automatiquement
        const employeeEmail = searchParams.get('employee');
        const month = searchParams.get('month');
        
        if (employeeEmail) {
          setSelectedEmployee(employeeEmail);
          console.log('🔍 Filtre employé appliqué:', employeeEmail);
        }
        
        if (month) {
          setSelectedMonth(month);
          console.log('🔍 Filtre mois appliqué:', month);
        }
        
        setShowValidationBanner(true);
        console.log('✅ Mode validation activé');
        
      } catch (error) {
        console.error('❌ Erreur chargement feuille:', error);
      }
    };

    loadTimesheetForValidation();
  }, [timesheetId, searchParams]);

  const fetchEmployees = async () => {
    console.log("Fetching employees...");
    const snapshot = await getDocs(collection(db, "employees"));
    const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    console.log("Employees loaded:", list.length);
    setEmployees(list);
  };

  const fetchSites = async () => {
    console.log("Fetching sites...");
    const snapshot = await getDocs(collection(db, "sites"));
    const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    console.log("Sites loaded:", list.length);
    setSites(list);
  };

  const fetchSitesHistory = async () => {
    try {
      const sitesSnap = await getDocs(collection(db, "sitesHistory"));
      const sitesData = sitesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSitesHistory(sitesData);
    } catch (error) {
      console.error("Erreur lors de la récupération des sites:", error);
    }
  };

  const fetchNewAbsenceRequests = async () => {
    try {
      const absencesRef = collection(db, "absences");
      const q = query(absencesRef, where("status", "==", "En attente"));
      const snapshot = await getDocs(q);
      setNewAbsenceRequests(snapshot.size);
    } catch (error) {
      console.error("Erreur lors du chargement des demandes d'absence:", error);
    }
  };


  const formatTime = (ms) => {
    if (!ms || isNaN(ms)) return '0h 0m';
    const totalMinutes = Math.round(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatTimeHHMM = (timestamp) => {
    if (!timestamp) return '';
    if (typeof timestamp === 'number') {
      // Si c'est un timestamp en secondes
      const date = new Date(timestamp * 1000);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } else if (timestamp.seconds !== undefined) {
      // Si c'est un timestamp Firestore
      const date = new Date(timestamp.seconds * 1000);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } else if (timestamp.toDate) {
      // Si c'est un timestamp Firestore avec méthode toDate()
      const date = timestamp.toDate();
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
    return '';
  };

  const getTimeInSeconds = (session) => {
    if (session.offlineStartTime) {
      return Math.floor(new Date(session.offlineStartTime).getTime() / 1000);
    }
    if (session.startTime) {
      return session.startTime.seconds;
    }
    return 0;
  };

  const filteredSessions = workSessions
    .filter((session) => {
      if (selectedEmployee && session.employeeEmail !== selectedEmployee) return false;

      // Obtenir le timestamp en secondes
      const startTimeSeconds = getTimeInSeconds(session);

      if (!startTimeSeconds) {
        console.warn('Session sans timestamp de début valide:', session);
        return false;
      }

      // Plus besoin de filtrer par mois ici car c'est fait lors du chargement depuis Firebase
      return true;
    })
    .sort((a, b) => getTimeInSeconds(b) - getTimeInSeconds(a));

  // Pagination sur les sessions filtrées
  const pagedSessions = filteredSessions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalSessions = filteredSessions.length;

  const exportToCSV = async () => {
    try {
      // Récupérer la configuration CSV depuis Firestore
      const configRef = doc(db, "adminConfigs", "global");
      const configSnap = await getDoc(configRef);
      let csvColumns;
      
      if (configSnap.exists()) {
        const data = configSnap.data();
        csvColumns = data.csvColumns || ADMIN_COLUMNS.map(col => ({ ...col, checked: true }));
      } else {
        csvColumns = ADMIN_COLUMNS.map(col => ({ ...col, checked: true }));
      }

      // Déclarer keyMapping ici
      const keyMapping = {
        'nom': 'name',
        'date': 'date',
        'heureDebut': 'startTime',
        'heureFin': 'endTime',
        'travail': 'workTime',
        'pause': 'pauseTime',
        'site': 'sites',
        'gpsDebut': 'gpsStart',
        'gpsFin': 'gpsEnd',
        'zoneDebut': 'zoneStart',
        'zoneFin': 'zoneEnd',
        'zonePause': 'zonePause',
        'appareil': 'device',
        'info': 'info'
      };

      const rows = [];
      // En-tête avec uniquement les colonnes sélectionnées
      const header = [];
      csvColumns.forEach(col => {
        if (col.checked) {
          header.push(col.label);
        }
      });
      rows.push(header);

      pagedSessions.forEach((session) => {
        const row = [];
        csvColumns.forEach(col => {
          if (!col.checked) return;
          const englishKey = keyMapping[col.key] || col.key;
          const startTime = session.startTime?.seconds ? new Date(session.startTime.seconds * 1000) : null;
          const endTime = session.endTime?.seconds ? new Date(session.endTime.seconds * 1000) : null;
          switch (englishKey) {
            case 'name':
              // Utiliser la nouvelle fonction pour le nom
              row.push(getEmployeeDisplayName(session));
              break;
            case 'date':
              row.push(startTime ? startTime.toLocaleDateString('fr-FR') : 'Non disponible');
              break;
            case 'startTime':
              row.push(startTime ? startTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'Non disponible');
              break;
            case 'endTime':
              row.push(endTime ? endTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'En cours');
              break;
            case 'workTime':
              if (!startTime || !endTime) row.push('Non disponible');
              else {
                row.push(getWorkDuration(session));
              }
              break;
            case 'pauseTime':
              row.push(getPauseDuration(session));
              break;
            case 'sites':
              // Utiliser la nouvelle fonction pour les sites autorisés
              row.push(getEmployeeAuthorizedSites(session));
              break;
            case 'gpsStart':
              if (!session.startLocation) row.push('Non disponible');
              else row.push(`Lat: ${formatGPSCoordinate(session.startLocation, "latitude")}, Long: ${formatGPSCoordinate(session.startLocation, "longitude")}`);
              break;
            case 'gpsEnd':
              if (!session.endLocation) row.push('Non disponible');
              else row.push(`Lat: ${formatGPSCoordinate(session.endLocation, "latitude")}, Long: ${formatGPSCoordinate(session.endLocation, "longitude")}`);
              break;
            case 'zoneStart':
              row.push(getZoneLabelSimple(session.startLocation, sites));
              break;
            case 'zoneEnd':
              row.push(getZoneLabelSimple(session.endLocation, sites));
              break;
            case 'zonePause':
              row.push(getPauseZonesForExport(session, sites));
              break;
            case 'device':
              row.push(formatDeviceInfo(session.device));
              break;
            case 'info':
              row.push(session.isManualAdjustment || session.isEscalationValidated ? 'Manuel' : 'Auto');
              break;
            default:
              row.push("");
          }
        });
        rows.push(row);
      });

      // Calculer les totaux
      const totalWorkTime = formatTime(
        pagedSessions.reduce((total, session) => {
          if (session.adjustedWorkTime != null) {
            return total + session.adjustedWorkTime;
          }
          if (session.startTime?.seconds && session.endTime?.seconds) {
            return total + Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
          }
          return total;
        }, 0)
      );

      const totalPauseTime = formatTime(
        pagedSessions.reduce((total, session) => 
          total + (session.totalPauseTime || 0), 0
        )
      );

      // Ajouter la ligne de total
      const totalRow = [];
      csvColumns.forEach(col => {
        if (!col.checked) return;
        const englishKey = keyMapping[col.key] || col.key;
        switch (englishKey) {
          case 'name':
            totalRow.push(`TOTAL (${pagedSessions.length} sessions)`);
            break;
          case 'workTime':
            totalRow.push(totalWorkTime);
            break;
          case 'pauseTime':
            totalRow.push(totalPauseTime);
            break;
          default:
            totalRow.push("-");
        }
      });
      rows.push(totalRow);

      // Ajout du BOM UTF-8 pour Excel
      const csvContent = "\uFEFF" + rows.map((r) => r.join(";")).join("\n");
      const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "suivi_salaries.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur lors de l'export CSV:", error);
      alert(`Une erreur est survenue lors de l'export CSV: ${error.message}`);
    }
  };

  // Fonction utilitaire pour charger une image en DataURL et obtenir ses dimensions
  async function loadImageData(url) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        // Créer un canvas pour convertir en DataURL
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        resolve({ dataUrl, width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  // Versions "texte" pour le PDF (même logique que getStartTimeDisplay/getEndTimeDisplay)
  const getStartTimeDisplayText = (session) => {
    const real = session.startTime?.seconds ? formatTimeHHMM(session.startTime) : '';
    const adjusted = session.adjustedStartTime ? formatTimeHHMM(session.adjustedStartTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedStartTime) {
      return adjusted;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real && real) {
      return `${adjusted} / ${real}`;
    } else {
      return real || 'Non disponible';
    }
  };

  const getEndTimeDisplayText = (session) => {
    const real = session.endTime?.seconds ? formatTimeHHMM(session.endTime) : 'En cours';
    const adjusted = session.adjustedEndTime ? formatTimeHHMM(session.adjustedEndTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedEndTime) {
      return adjusted;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real && real !== 'En cours') {
      return `${adjusted} / ${real}`;
    } else {
      return real;
    }
  };
  const exportToPDF = async () => {
    try {
      // ── Config depuis Firestore ──────────────────────────────────────────────
      const configRef  = doc(db, "adminConfigs", "global");
      const configSnap = await getDoc(configRef);
      let pdfColumns;
      let pdfShowHoraeLogo = false;

      if (configSnap.exists()) {
        const data = configSnap.data();
        pdfColumns       = data.pdfColumns || ADMIN_COLUMNS.map(col => ({ ...col, checked: true, width: Math.round(100 / ADMIN_COLUMNS.length) }));
        pdfShowHoraeLogo = !!data.pdfShowHoraeLogo;
      } else {
        pdfColumns = ADMIN_COLUMNS.map(col => ({ ...col, checked: true, width: Math.round(100 / ADMIN_COLUMNS.length) }));
      }

      const pdfOrientation = configSnap.exists() && configSnap.data().pdfOrientation
        ? configSnap.data().pdfOrientation : 'landscape';

      // ── Infos mois & employé ────────────────────────────────────────────────
      const [yearStr, monthNumStr] = selectedMonth.split('-');
      const year = parseInt(yearStr);
      const monthNum = parseInt(monthNumStr);
      const MONTH_NAMES_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
      const monthLabel = `${MONTH_NAMES_FR[monthNum - 1]} ${year}`;

      const empLabel = selectedEmployee
        ? (() => { const e = employees.find(x => x.email === selectedEmployee); return e ? `${e.prenom || ''} ${e.nom || ''}`.trim() : selectedEmployee; })()
        : 'Tous les salariés';

      // Nom de l'entreprise (champ "entreprise" du doc salarié)
      const companyName = (() => {
        const e = selectedEmployee
          ? employees.find(x => x.email === selectedEmployee)
          : employees[0];
        return e?.entreprise || '';
      })();

      // Formatage de date sans caractères Unicode parasites (pour jsPDF)
      const dateFR = (date) => {
        const d = new Date(date);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };

      // ── Grouper toutes les sessions filtrées par semaine ────────────────────
      const getMonday = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
        d.setHours(0, 0, 0, 0);
        return d;
      };
      const getSunday = (monday) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + 6);
        d.setHours(23, 59, 59, 999);
        return d;
      };

      const weekMap = {};
      filteredSessions.forEach(session => {
        const st = session.startTime?.seconds ? new Date(session.startTime.seconds * 1000) : null;
        if (!st) return;
        const monday = getMonday(st);
        const key = monday.toISOString().slice(0, 10);
        if (!weekMap[key]) weekMap[key] = { monday, sessions: [] };
        weekMap[key].sessions.push(session);
      });
      const weeks = Object.values(weekMap).sort((a, b) => a.monday - b.monday);

      if (weeks.length === 0) {
        alert('Aucune session à exporter pour ce mois / cet employé.');
        return;
      }

      // ── Colonnes ────────────────────────────────────────────────────────────
      const keyMapping = {
        'nom': 'name', 'date': 'date', 'heureDebut': 'startTime', 'heureFin': 'endTime',
        'travail': 'workTime', 'pause': 'pauseTime', 'site': 'sites',
        'gpsDebut': 'gpsStart', 'gpsFin': 'gpsEnd',
        'zoneDebut': 'zoneStart', 'zoneFin': 'zoneEnd', 'appareil': 'device', 'info': 'info'
      };

      const checkedColumns = pdfColumns.filter(col => col.checked);
      const columnHeaders  = checkedColumns.map(col =>
        col.key === 'startTime' ? 'H. Début (Ret./Réel)' :
        col.key === 'endTime'   ? 'H. Fin (Ret./Réel)' : col.label
      );

      // Résumé lisible du user agent pour le PDF
      const formatDeviceShort = (device) => {
        if (!device) return '—';
        const ua = device.userAgent || '';
        let type = '';
        if (/iPhone/i.test(ua))           type = 'iPhone';
        else if (/iPad/i.test(ua))        type = 'iPad';
        else if (/Android/i.test(ua))     type = 'Android';
        else if (/Windows/i.test(ua))     type = 'Windows';
        else if (/Macintosh|Mac OS/i.test(ua)) type = 'Mac';
        else                              type = device.platform || '—';
        let browser = '';
        if (/Firefox/i.test(ua))          browser = 'Firefox';
        else if (/SamsungBrowser/i.test(ua)) browser = 'Samsung';
        else if (/Chrome/i.test(ua))      browser = 'Chrome';
        else if (/Safari/i.test(ua))      browser = 'Safari';
        const res = device.screenResolution ? ` (${device.screenResolution})` : '';
        return `${type}${res}${browser ? ' / ' + browser : ''}`;
      };

      const sessionToRow = (session) => checkedColumns.map(col => {
        const k = keyMapping[col.key] || col.key;
        const st = session.startTime?.seconds ? new Date(session.startTime.seconds * 1000) : null;
        const et = session.endTime?.seconds   ? new Date(session.endTime.seconds * 1000)   : null;
        switch (k) {
          case 'name':     return getEmployeeDisplayName(session);
          case 'date':     return st ? dateFR(st) : '—';
          case 'startTime': return getStartTimeDisplayText(session);
          case 'endTime':  return getEndTimeDisplayText(session);
          case 'workTime': return (st && et) ? getWorkDuration(session) : '—';
          case 'pauseTime': return getPauseDuration(session);
          case 'sites':    return getEmployeeAuthorizedSites(session);
          case 'gpsStart': return session.startLocation ? `${formatGPSCoordinate(session.startLocation,"latitude")}, ${formatGPSCoordinate(session.startLocation,"longitude")}` : '—';
          case 'gpsEnd':   return session.endLocation   ? `${formatGPSCoordinate(session.endLocation,"latitude")}, ${formatGPSCoordinate(session.endLocation,"longitude")}` : '—';
          case 'zoneStart': return getZoneLabelSimple(session.startLocation, sites);
          case 'zoneEnd':   return getZoneLabelSimple(session.endLocation, sites);
          case 'zonePause': return getPauseZonesForExport(session, sites);
          case 'device':    return formatDeviceShort(session.device);
          case 'info':      return session.isManualAdjustment || session.isEscalationValidated ? 'Manuel' : 'Auto';
          default:          return '—';
        }
      });

      const calcWorkMs = (session) => {
        if (session.adjustedWorkTime != null) return session.adjustedWorkTime;
        if (session.adminModified && session.adjustedStartTime && session.adjustedEndTime) {
          const s = session.adjustedStartTime instanceof Date ? session.adjustedStartTime.getTime() : new Date(session.adjustedStartTime).getTime();
          const e = session.adjustedEndTime instanceof Date   ? session.adjustedEndTime.getTime()   : new Date(session.adjustedEndTime).getTime();
          return Math.max(0, e - s - (session.totalPauseTime || 0));
        }
        if (session.startTime?.seconds && session.endTime?.seconds)
          return Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
        return 0;
      };

      const totalRow = (sessions, label) => checkedColumns.map(col => {
        const k = keyMapping[col.key] || col.key;
        if (k === 'name')     return label;
        if (k === 'workTime') return formatTime(sessions.reduce((s, x) => s + calcWorkMs(x), 0));
        if (k === 'pauseTime') return formatTime(sessions.reduce((s, x) => s + (x.totalPauseTime || 0), 0));
        return '—';
      });

      // ── Créer le PDF ────────────────────────────────────────────────────────
      const pdfDoc = new jsPDF({ orientation: pdfOrientation });
      const pageW   = pdfDoc.internal.pageSize.getWidth();
      const margin  = 14;
      const tableW  = pageW - margin * 2;
      const columnStyles = {};
      checkedColumns.forEach((col, idx) => {
        columnStyles[idx] = { cellWidth: (col.width / 100) * tableW };
      });

      // Charger logo si demandé
      let logoData = null;
      if (pdfShowHoraeLogo) {
        try { logoData = await loadImageData('/logo.png'); } catch (_) {}
      }

      const addPageHeader = (weekLabel, isFirst) => {
        if (!isFirst) pdfDoc.addPage();
        // Logo
        if (logoData) {
          const lh = 8; const lw = Math.round(lh * logoData.width / logoData.height);
          pdfDoc.addImage(logoData.dataUrl, 'PNG', pageW - margin - lw, 8, lw, lh, undefined, 'FAST');
        }
        // Titre
        pdfDoc.setFontSize(13);
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.text('RAPPORT DE POINTAGE', margin, 14);
        pdfDoc.setFontSize(9);
        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.text(`${monthLabel}  —  ${empLabel}`, margin, 20);
        pdfDoc.setDrawColor(200);
        pdfDoc.line(margin, 23, pageW - margin, 23);
        // Sous-titre semaine
        pdfDoc.setFontSize(10);
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.setTextColor(76, 83, 232);
        pdfDoc.text(weekLabel, margin, 30);
        pdfDoc.setTextColor(0);
        pdfDoc.setFont('helvetica', 'normal');
      };

      // ── Une section par semaine ─────────────────────────────────────────────
      let globalWork = 0;
      let globalPause = 0;

      weeks.forEach((week, wi) => {
        const sunday    = getSunday(week.monday);
        const weekLabel = `Semaine du ${dateFR(week.monday)} au ${dateFR(sunday)} (${week.sessions.length} session${week.sessions.length > 1 ? 's' : ''})`;
        addPageHeader(weekLabel, wi === 0);

        const wWork  = week.sessions.reduce((s, x) => s + calcWorkMs(x), 0);
        const wPause = week.sessions.reduce((s, x) => s + (x.totalPauseTime || 0), 0);
        globalWork  += wWork;
        globalPause += wPause;

        const rows = [
          ...week.sessions.map(sessionToRow),
          totalRow(week.sessions, `TOTAL SEMAINE (${week.sessions.length} sessions)`),
        ];

        autoTable(pdfDoc, {
          head: [columnHeaders],
          body: rows,
          startY: 34,
          styles: { fontSize: 7.5 },
          headStyles: { fillColor: [76, 83, 232], textColor: 255, fontSize: 7.5 },
          alternateRowStyles: { fillColor: [248, 248, 255] },
          theme: 'grid',
          columnStyles,
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section === 'body' && data.row.index === rows.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [230, 230, 255];
            }
          },
        });

        // ── Signatures ──
        const sigY = pdfDoc.lastAutoTable.finalY + 10;
        pdfDoc.setFontSize(9);
        pdfDoc.text('Signature du salarié :', margin, sigY);
        pdfDoc.rect(margin, sigY + 3, 76, 18);
        pdfDoc.text("Signature de l'employeur :", margin + 96, sigY);
        pdfDoc.rect(margin + 96, sigY + 3, 76, 18);
        pdfDoc.setFontSize(7);
        pdfDoc.setTextColor(150);
        pdfDoc.text('Document à conserver 5 ans — Art. L3171-3 Code du travail', pageW / 2, sigY + 26, { align: 'center' });
        pdfDoc.setTextColor(0);
      });

      // ── Dernière page : récap mensuel ───────────────────────────────────────
      pdfDoc.addPage();
      if (logoData) {
        const lh = 8; const lw = Math.round(lh * logoData.width / logoData.height);
        pdfDoc.addImage(logoData.dataUrl, 'PNG', pageW - margin - lw, 8, lw, lh, undefined, 'FAST');
      }
      pdfDoc.setFontSize(14);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('RÉCAPITULATIF MENSUEL', pageW / 2, 16, { align: 'center' });
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.text(`${monthLabel}  —  ${empLabel}`, pageW / 2, 23, { align: 'center' });
      if (companyName) {
        pdfDoc.setFontSize(9);
        pdfDoc.setTextColor(100);
        pdfDoc.text(`Entreprise : ${companyName}`, pageW / 2, 29, { align: 'center' });
        pdfDoc.setTextColor(0);
        pdfDoc.setFontSize(10);
      }
      pdfDoc.setDrawColor(200);
      pdfDoc.line(margin, companyName ? 32 : 26, pageW - margin, companyName ? 32 : 26);

      const recapRows = weeks.map((week, wi) => {
        const sunday = getSunday(week.monday);
        const wWork  = week.sessions.reduce((s, x) => s + calcWorkMs(x), 0);
        const wPause = week.sessions.reduce((s, x) => s + (x.totalPauseTime || 0), 0);
        return [
          `Semaine ${wi + 1}`,
          `${dateFR(week.monday)} au ${dateFR(sunday)}`,
          `${week.sessions.length} session(s)`,
          formatTime(wPause),
          formatTime(wWork),
        ];
      });
      recapRows.push([
        { content: 'TOTAL MENSUEL', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [76,83,232], textColor: 255 } },
        { content: `${filteredSessions.length} session(s)`, styles: { fontStyle: 'bold', fillColor: [76,83,232], textColor: 255 } },
        { content: formatTime(globalPause), styles: { fontStyle: 'bold', fillColor: [76,83,232], textColor: 255 } },
        { content: formatTime(globalWork),  styles: { fontStyle: 'bold', fillColor: [76,83,232], textColor: 255 } },
      ]);

      autoTable(pdfDoc, {
        head: [['', 'Période', 'Sessions', 'Tps pause', 'Tps travail']],
        body: recapRows,
        startY: companyName ? 36 : 30,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [50,50,50], textColor: 255, fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 74 }, 2: { cellWidth: 30 }, 3: { cellWidth: 28 }, 4: { cellWidth: 28 } },
        margin: { left: margin, right: margin },
      });

      const rSigY = pdfDoc.lastAutoTable.finalY + 14;
      pdfDoc.setFontSize(10);
      pdfDoc.text('Signature du salarié :', margin, rSigY);
      pdfDoc.rect(margin, rSigY + 3, 76, 22);
      pdfDoc.text("Signature de l'employeur :", margin + 96, rSigY);
      pdfDoc.rect(margin + 96, rSigY + 3, 76, 22);
      pdfDoc.setFontSize(7);
      pdfDoc.setTextColor(150);
      pdfDoc.text('Document à conserver 5 ans — Art. L3171-3 Code du travail', pageW / 2, rSigY + 32, { align: 'center' });

      // ── Sauvegarder ─────────────────────────────────────────────────────────
      const filename = `pointages_${empLabel.replace(/\s+/g,'_')}_${monthLabel.replace(/\s+/g,'-')}.pdf`;
      pdfDoc.save(filename);

    } catch (error) {
      console.error("Erreur export PDF:", error);
      alert(`Erreur export PDF: ${error.message}`);
    }
  };

  // Générer la liste des 12 derniers mois (fixe, indépendamment des sessions)
  const months = (() => {
    const monthsList = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
      monthsList.push(monthKey);
    }
    
    return monthsList;
  })();

  const getWorkDuration = (session) => {
    // PRIORITÉ 1 : Utiliser adjustedWorkTime s'il existe (calculé lors de la sauvegarde)
    if (session.adjustedWorkTime != null) {
      return formatTime(session.adjustedWorkTime);
    }
    
    // PRIORITÉ 2 : Si l'horaire a été modifié manuellement mais pas de adjustedWorkTime, recalculer
    if (session.adminModified && session.adjustedStartTime && session.adjustedEndTime) {
      const startTime = session.adjustedStartTime instanceof Date ? session.adjustedStartTime.getTime() : new Date(session.adjustedStartTime).getTime();
      const endTime = session.adjustedEndTime instanceof Date ? session.adjustedEndTime.getTime() : new Date(session.adjustedEndTime).getTime();
      const workTimeMs = Math.max(0, endTime - startTime - (session.totalPauseTime || 0));
      return formatTime(workTimeMs);
    }
    
    // PRIORITÉ 3 : Utiliser les horaires originaux
    if (session.startTime?.seconds && session.endTime?.seconds) {
      const workTimeMs = Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
      return formatTime(workTimeMs);
    }
    return '0h 0m';
  };

  const getPauseDuration = (session) => {
    return formatTime(session.totalPauseTime || 0);
  };

  const getStartTimeDisplay = (session) => {
    const real = session.startTime?.seconds ? formatTimeHHMM(session.startTime) : '';
    const adjusted = session.adjustedStartTime ? formatTimeHHMM(session.adjustedStartTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedStartTime) {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real) {
      return (
        <>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>
          <div style={{ color: '#444', fontSize: '0.85em' }}>{real}</div>
        </>
      );
    } else {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{real}</div>;
    }
  };

  const getEndTimeDisplay = (session) => {
    const real = session.endTime?.seconds ? formatTimeHHMM(session.endTime) : 'En cours';
    const adjusted = session.adjustedEndTime ? formatTimeHHMM(session.adjustedEndTime) : real;
    
    // Si l'horaire a été modifié manuellement, il reste "réel" (pas pondéré)
    if (session.adminModified && session.adjustedEndTime) {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>;
    }
    
    // Sinon, distinction normale entre pondéré et réel
    if (adjusted !== real) {
      return (
        <>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{adjusted}</div>
          <div style={{ color: '#444', fontSize: '0.85em' }}>{real}</div>
        </>
      );
    } else {
      return <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{real}</div>;
    }
  };

  // Correction de l'affichage GPS et zones
  const getGPSLink = (location) => {
    const lat = getLat(location);
    const lng = getLng(location);
    if (!lat || !lng) return <span className="no-location">Non disponible</span>;
    return (
      <a
        href={`https://www.google.com/maps?q=${lat},${lng}`}
        target="_blank"
        rel="noopener noreferrer"
        className="map-link"
      >
        {`Lat: ${lat}, Long: ${lng}`}
      </a>
    );
  };

  const getCoord = (location) => {
    if (!location) return null;
    if (typeof location.lat === 'number' && typeof location.lng === 'number') {
      return { lat: location.lat, lng: location.lng };
    }
    if (typeof location.latitude === 'number' && typeof location.longitude === 'number') {
      return { lat: location.latitude, lng: location.longitude };
    }
    return null;
  };

  const getBreakLocations = (session) => {
    if (!session.breaks || session.breaks.length === 0) return "Aucune pause";

    // Trie décroissant (plus récent en haut)
    const sortedBreaks = [...session.breaks].sort((a, b) => {
      const aTime = a.startTime?.toDate ? a.startTime.toDate().getTime() : new Date(a.startTime).getTime();
      const bTime = b.startTime?.toDate ? b.startTime.toDate().getTime() : new Date(b.startTime).getTime();
      return bTime - aTime;
    });

    return sortedBreaks.map(breakItem => {
      const startCoord = getCoord(breakItem.startLocation);
      const endCoord = getCoord(breakItem.endLocation);
      const startTime = breakItem.startTime?.toDate ? breakItem.startTime.toDate() : new Date(breakItem.startTime);
      const endTime = breakItem.endTime?.toDate ? breakItem.endTime.toDate() : new Date(breakItem.endTime);
      
      const formatLocation = (coord, time, label, siteId) => {
        let lieu = null;
        let colorClass = '';
        let isHorsZone = false;
        let lien = null;
        let site = null;
        
        // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
        if (siteId) {
          // Cas spécial : HORS_ZONE
          if (siteId === 'HORS_ZONE') {
            lieu = "HORS ZONE";
            colorClass = 'zone-ko';
            isHorsZone = true;
          } else {
            // Cas normal : chercher le site correspondant
            site = sites.find(s => s.id === siteId);
            if (site) {
              lieu = site.nom;
              colorClass = 'zone-ok';
            }
          }
        }
        
        // PRIORITÉ 2 : Si pas de siteId ou site non trouvé (et pas HORS_ZONE), calculer à partir des coordonnées
        if (!site && !isHorsZone && coord) {
          site = findPositionSite({ latitude: coord.lat, longitude: coord.lng });
          if (site) {
            lieu = site.nom;
            colorClass = 'zone-ok';
          }
        }
        
        // Si toujours pas de site trouvé et pas HORS_ZONE
        if (!site && !isHorsZone) {
          lieu = "HORS ZONE";
          colorClass = 'zone-ko';
          isHorsZone = true;
        }
        
        // Lien carte uniquement pour HORS ZONE
        if (isHorsZone && coord) {
          lien = (
            <a 
              href={`https://www.google.com/maps?q=${coord.lat},${coord.lng}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="zone-link"
              style={{ 
                display: 'block',
                margin: '0 auto',
                textAlign: 'center',
                color: '#1976d2', 
                textDecoration: 'none', 
                fontWeight: 'normal',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            >
              📍Carte
            </a>
          );
        }
        
        return (
          <div style={{ textAlign: 'center' }}>
            <div>
              <span style={{ 
                color: colorClass === 'zone-ok' ? '#4caf50' : 
                       colorClass === 'zone-ko' ? '#f44336' : '#ff9800',
                fontWeight: 'bold'
              }}>
                {label} {time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <div style={{ color: colorClass === 'zone-ok' ? '#4caf50' : colorClass === 'zone-ko' ? '#f44336' : '#ff9800' }}>
              {lieu}
            </div>
            {lien && (
              <div style={{ textAlign: 'center' }}>
                {lien}
              </div>
            )}
          </div>
        );
      };

      return (
        <div key={breakItem.id} className="break-location">
          <div>{formatLocation(startCoord, startTime, 'D', breakItem.startLocation?.siteId)}</div>
          <div>{formatLocation(endCoord, endTime, 'F', breakItem.endLocation?.siteId)}</div>
        </div>
      );
    });
  };

  // Fonction pour obtenir le nom correct de l'employé (prénom nom)
  const getEmployeeDisplayName = (session) => {
    const employee = employees.find(emp => emp.email === session.employeeEmail);
    if (employee) {
      return `${employee.prenom} ${employee.nom}`;
    }
    return session.employeeName || session.employeeEmail || "Inconnu";
  };

  // Fonction pour obtenir tous les sites autorisés de l'employé
  const getEmployeeAuthorizedSites = (session) => {
    const employee = employees.find(emp => emp.email === session.employeeEmail);
    if (employee && employee.siteDeTravail) {
      const authorizedSiteIds = Array.isArray(employee.siteDeTravail) ? employee.siteDeTravail : [employee.siteDeTravail];
      const authorizedSites = authorizedSiteIds
        .map(siteId => {
          const site = sites.find(s => s.id === siteId);
          return site ? site.nom : "Site inconnu";
        })
        .join(", ");
      return authorizedSites;
    }
    return "Non défini";
  };

  // Fonction pour obtenir le site de début/fin
  const getSessionSite = (session, type) => {
    console.log(`🎯 SITE_DEBUG getSessionSite appelé avec type: ${type}`);
    console.log(`🎯 SITE_DEBUG session.startLocation:`, session.startLocation);
    console.log(`🎯 SITE_DEBUG session.endLocation:`, session.endLocation);
    
    if (type === 'start') {
      const siteId = session.startLocation?.siteId || session.siteDeTravail;
      console.log(`🎯 SITE_DEBUG start - siteId: ${siteId}`);
      if (siteId === 'HORS_ZONE') {
        console.log(`🎯 SITE_DEBUG start - HORS_ZONE détecté, retour: HORS ZONE`);
        return 'HORS ZONE';
      }
      const site = sites.find(s => s.id === siteId);
      const result = site ? site.nom : siteId || 'Non disponible';
      console.log(`🎯 SITE_DEBUG start - résultat: ${result}`);
      return result;
    } else if (type === 'end') {
      const siteId = session.endLocation?.siteId || session.endSite;
      console.log(`🎯 SITE_DEBUG end - siteId: ${siteId}`);
      if (siteId === 'HORS_ZONE') {
        console.log(`🎯 SITE_DEBUG end - HORS_ZONE détecté, retour: HORS ZONE`);
        return 'HORS ZONE';
      }
      const site = sites.find(s => s.id === siteId);
      const result = site ? site.nom : siteId || 'Non disponible';
      console.log(`🎯 SITE_DEBUG end - résultat: ${result}`);
      return result;
    }
    return 'Non disponible';
  };

  // Fonction utilitaire pour afficher la zone (début/fin) avec couleur et lien si hors zone
  const renderZoneCell = (location, employee, sites, label) => {
    if (!location) return <span style={{ color: 'red' }}>Non disponible</span>;
    
    // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
    if (location.siteId) {
      // Cas spécial : HORS_ZONE
      if (location.siteId === 'HORS_ZONE') {
        const lat = getLat(location);
        const lng = getLng(location);
        return (
          <div style={{ color: 'red', fontWeight: 'bold' }}>
            HORS ZONE
            {lat && lng && (
              <div>
                <a
                  href={`https://www.google.com/maps?q=${lat},${lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#1976d2', 
                    textDecoration: 'none', 
                    fontWeight: 'normal',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  📍Carte
                </a>
              </div>
            )}
          </div>
        );
      }
      
      // Cas normal : chercher le site correspondant
      const site = sites.find(s => s.id === location.siteId);
      if (site) {
        const authorizedSiteIds = employee && employee.siteDeTravail
          ? (Array.isArray(employee.siteDeTravail) ? employee.siteDeTravail : [employee.siteDeTravail])
          : [];
        const isAuthorized = authorizedSiteIds.includes(site.id);
        return (
          <span style={{ color: isAuthorized ? 'green' : 'red', fontWeight: 'bold' }}>
            {site.nom}
          </span>
        );
      }
    }
    
    // PRIORITÉ 2 : Chercher le site correspondant à la position GPS
    const actualSite = findPositionSite(location);
    const authorizedSiteIds = employee && employee.siteDeTravail
      ? (Array.isArray(employee.siteDeTravail) ? employee.siteDeTravail : [employee.siteDeTravail])
      : [];
    
    if (actualSite) {
      const isAuthorized = authorizedSiteIds.includes(actualSite.id);
      return (
        <span style={{ color: isAuthorized ? 'green' : 'red', fontWeight: 'bold' }}>
          {actualSite.nom}
        </span>
      );
    } else {
      // HORS ZONE
      const lat = getLat(location);
      const lng = getLng(location);
      return (
        <div style={{ color: 'red', fontWeight: 'bold' }}>
          HORS ZONE
          {lat && lng && (
            <div>
              <a
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none', 
                  fontWeight: 'normal',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                📍Carte
              </a>
            </div>
          )}
        </div>
      );
    }
  };

  // Fonction utilitaire pour PDF/CSV : nom du site ou HORS ZONE
  function getZoneLabelSimple(location, sites) {
    if (!location) return "Non disponible";
    // Priorité au siteId si présent
    if (location.siteId) {
      const site = sites.find(s => s.id === location.siteId);
      return site ? site.nom : "HORS ZONE";
    }
    const actualSite = findPositionSite(location);
    return actualSite ? actualSite.nom : "HORS ZONE";
  }

  function getPauseZonesForExport(session, sites) {
    if (!session.breaks || !Array.isArray(session.breaks) || session.breaks.length === 0) return "";
    return session.breaks.map(breakItem => {
      // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
      const siteId = breakItem.siteId;
      if (siteId === "HORS_ZONE") return "HORS ZONE";
      
      // PRIORITÉ 2 : Utiliser le siteId des locations si disponible
      const startSiteId = breakItem.startLocation?.siteId;
      const endSiteId = breakItem.endLocation?.siteId;
      
      if (startSiteId === "HORS_ZONE" || endSiteId === "HORS_ZONE") return "HORS ZONE";
      
      // PRIORITÉ 3 : Chercher les sites par ID
      let startSite = startSiteId ? sites.find(s => s.id === startSiteId) : null;
      let endSite = endSiteId ? sites.find(s => s.id === endSiteId) : null;
      
      // PRIORITÉ 4 : Si pas trouvé par siteId, calculer à partir des coordonnées (comme dans getBreakLocations)
      if (!startSite && breakItem.startLocation?.latitude && breakItem.startLocation?.longitude) {
        startSite = findPositionSite(breakItem.startLocation);
      }
      if (!endSite && breakItem.endLocation?.latitude && breakItem.endLocation?.longitude) {
        endSite = findPositionSite(breakItem.endLocation);
      }
      
      const startSiteName = startSite ? startSite.nom : "HORS ZONE";
      const endSiteName = endSite ? endSite.nom : "HORS ZONE";
      
      // Si les sites sont différents, afficher les deux
      if (startSiteName !== endSiteName) {
        return `${startSiteName} → ${endSiteName}`;
      }
      
      return startSiteName;
    }).join(", ");
  }

  // Fonction pour ouvrir le modal de modification
  const handleEditSession = (session) => {
    const timestampToDateTimeLocal = (timestamp) => {
      
      if (!timestamp) {
        return '';
      }
      
      let date;
      
      if (timestamp.toDate) {
        // Timestamp Firestore avec méthode toDate()
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        // Timestamp avec seconds/nanoseconds
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'number') {
        // Timestamp numérique
        date = new Date(timestamp > 1e12 ? timestamp : timestamp * 1000);
      } else if (timestamp instanceof Date) {
        // Objet Date
        date = timestamp;
      } else {
        return '';
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const result = `${year}-${month}-${day}T${hours}:${minutes}`;
      
      return result;
    };

    const getSiteFromLocation = (location) => {
      
      // PRIORITÉ 1 : Utiliser le siteId stocké dans les données
      if (location && location.siteId) {
        return location.siteId;
      }
      
      if (!location || !location.latitude || !location.longitude) {
        return null;
      }
      
      // PRIORITÉ 2 : Cache pour éviter les appels redondants
      const locationKey = `${location.latitude},${location.longitude}`;
      if (siteCache[locationKey] !== undefined) {
        return siteCache[locationKey];
      }
      
      // PRIORITÉ 3 : Chercher le site à partir des coordonnées GPS
      const site = findPositionSite(location);
      siteCache[locationKey] = site ? site.id : null;
      
      if (site) {
      } else {
      }
      
      return site ? site.id : null;
    };

    // Pré-remplir les données du modal
    const startDateStr = session.startTime ? timestampToDateTimeLocal(session.startTime).split('T')[0] : '';
    const prefillData = {
      startDate: startDateStr,
      startTime: session.startTime ? timestampToDateTimeLocal(session.startTime).split('T')[1] : '',
      startSite: session.startLocation?.siteId || (session.startLocation ? getSiteFromLocation(session.startLocation) : ''),
      // Si pas d'endTime (session "En cours"), pré-remplir la date de fin avec la date de début
      // L'admin devra changer la date si la fin est après minuit (lendemain)
      endDate: session.endTime ? timestampToDateTimeLocal(session.endTime).split('T')[0] : startDateStr,
      endTime: session.endTime ? timestampToDateTimeLocal(session.endTime).split('T')[1] : '',
      endSite: session.endLocation?.siteId || (session.endLocation ? getSiteFromLocation(session.endLocation) : ''),
      breaks: session.breaks ? session.breaks.map(breakItem => {
        // Préserver "HORS_ZONE" si c'est déjà défini
        const startSiteId = breakItem.startLocation?.siteId || '';
        const endSiteId = breakItem.endLocation?.siteId || '';
        
        
        return {
          startTime: breakItem.startTime ? timestampToDateTimeLocal(breakItem.startTime) : '',
          endTime: breakItem.endTime ? timestampToDateTimeLocal(breakItem.endTime) : '',
          startLocation: {
            ...breakItem.startLocation,
            siteId: startSiteId || (breakItem.startLocation ? getSiteFromLocation(breakItem.startLocation) : '')
          },
          endLocation: {
            ...breakItem.endLocation,
            siteId: endSiteId || (breakItem.endLocation ? getSiteFromLocation(breakItem.endLocation) : '')
          }
        };
      }) : []
    };


    setEditSession(session);
    setEditData(prefillData);
    setShowEditModal(true);
  };

  // Fonction pour sauvegarder les modifications
  const handleSaveEdit = async () => {
    if (!editSession || isSaving) return; // Bloquer si déjà en cours de sauvegarde

    // Validation des champs obligatoires
    if (!editData.startDate || !editData.startTime) {
      alert('Veuillez saisir la date et l\'heure de début.');
      return;
    }
    if (!editData.endDate || !editData.endTime) {
      alert('Veuillez saisir la date et l\'heure de fin.\n\n⚠️ Si le salarié a fini après minuit (ex. 00h30), pensez à mettre la date de FIN au lendemain.');
      return;
    }

    try {
      setIsSaving(true); // Bloquer le bouton

      // Convertir les données du modal en format Firestore
      const startDateTime = new Date(`${editData.startDate}T${editData.startTime}`);
      const endDateTime = new Date(`${editData.endDate}T${editData.endTime}`);

      // Validation : fin doit être après le début
      if (endDateTime <= startDateTime) {
        alert('L\'heure de fin doit être après l\'heure de début.\n\n⚠️ Si le salarié a fini après minuit (ex. 00h30), il faut mettre la date de FIN au lendemain.');
        setIsSaving(false);
        return;
      }
      
      // Supprimer les anciennes pauses
      if (editSession.breaks && editSession.breaks.length > 0) {
        for (const breakItem of editSession.breaks) {
          if (breakItem.id) {
            await deleteDoc(doc(db, 'breaks', breakItem.id));
          }
        }
      }

      // Créer les nouvelles pauses
      const newBreaks = [];
      let totalPauseTime = 0;

      if (editData.breaks && editData.breaks.length > 0) {
        for (const breakItem of editData.breaks) {
          if (breakItem.startTime && breakItem.endTime) {
            const breakStartTime = new Date(breakItem.startTime);
            const breakEndTime = new Date(breakItem.endTime);
            const pauseDuration = breakEndTime.getTime() - breakStartTime.getTime();
            totalPauseTime += pauseDuration;

            const newBreak = {
              sessionId: editSession.id,
              startTime: Timestamp.fromDate(breakStartTime),
              endTime: Timestamp.fromDate(breakEndTime),
              startLocation: { ...(breakItem.startLocation || {}), siteId: breakItem.startLocation?.siteId || null },
              endLocation: { ...(breakItem.endLocation || {}), siteId: breakItem.endLocation?.siteId || null },
              createdAt: Timestamp.fromDate(new Date())
            };
            const breakRef = await addDoc(collection(db, 'breaks'), newBreak);
            newBreaks.push({ ...newBreak, id: breakRef.id });
          }
        }
      }

      // Recalculer le temps de travail ajusté avec les nouvelles valeurs
      const adjustedWorkTimeMs = Math.max(0, endDateTime.getTime() - startDateTime.getTime() - totalPauseTime);

      // Sauvegarder dans Firestore — on écrase TOUTES les valeurs pertinentes
      // IMPORTANT : adjustedStartTime / adjustedEndTime / adjustedWorkTime doivent être
      // mis à jour sinon l'affichage du tableau continuera d'afficher les anciennes valeurs
      await updateDoc(doc(db, 'workSessions', editSession.id), {
        startTime: Timestamp.fromDate(startDateTime),
        endTime: Timestamp.fromDate(endDateTime),
        adjustedStartTime: Timestamp.fromDate(startDateTime),
        adjustedEndTime: Timestamp.fromDate(endDateTime),
        adjustedWorkTime: adjustedWorkTimeMs,
        totalPauseTime,
        breaks: newBreaks,
        startLocation: { ...(editSession.startLocation || {}), siteId: editData.startSite },
        endLocation: { ...(editSession.endLocation || {}), siteId: editData.endSite },
        adminModified: true,
        adminModifiedAt: Timestamp.fromDate(new Date()),
      });

      // Appliquer les règles de tolérance (optionnel — ne bloque pas si ça échoue)
      try {
        const { workSessionService } = await import('../services/workSessionService');
        await workSessionService.applyToleranceRules(editSession.id);
      } catch (toleranceErr) {
        console.warn('applyToleranceRules (non bloquant):', toleranceErr.message);
      }

      // Recharger les sessions quoi qu'il arrive
      await fetchWorkSessions();

      setShowEditModal(false);
      setEditSession(null);
      setEditData({});

      alert('Session modifiée avec succès !');
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde : ' + error.message);
    } finally {
      setIsSaving(false); // Débloquer le bouton
    }
  };

  // Correction : recalcul dynamique du pré-remplissage quand sites sont chargés et modal ouvert
  useEffect(() => {
    if (showEditModal && editSession && sites.length > 0) {
      
      // Éviter la duplication : ne recalculer que si les pauses n'ont pas déjà été traitées
      if (editData.breaks && editData.breaks.length > 0) {
        // Vérifier si les pauses ont déjà des siteId corrects
        const needsUpdate = editData.breaks.some(breakItem => 
          !breakItem.startLocation?.siteId || !breakItem.endLocation?.siteId
        );
        
        if (!needsUpdate) {
          return;
        }
      }
      
      // Recalculer les sites pour les pauses
      const updatedBreaks = editData.breaks ? editData.breaks.map(breakItem => {
        const startSiteId = breakItem.startLocation?.siteId || '';
        const endSiteId = breakItem.endLocation?.siteId || '';
        
        
        return {
          ...breakItem,
          startLocation: {
            ...breakItem.startLocation,
            siteId: startSiteId
          },
          endLocation: {
            ...breakItem.endLocation,
            siteId: endSiteId
          }
        };
      }) : [];
      
      
      setEditData({
        ...editData,
        breaks: updatedBreaks
      });
    }
  }, [sites, showEditModal, editSession]);

  // ─── Création de session manuelle ────────────────────────────────────────────

  const openCreateModal = () => {
    // Charger la liste des employés si pas encore fait
    if (employees.length === 0) {
      getDocs(query(collection(db, 'employees'), orderBy('nom', 'asc')))
        .then(snap => setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
        .catch(console.error);
    }

    const today = new Date().toISOString().slice(0, 10);
    setCreateData({
      employeeEmail: '',
      startDate: today,
      startTime: '08:00',
      endDate: today,
      endTime: '17:00',
      breaks: [],
    });
    setShowCreateModal(true);
  };

  const handleSaveCreate = async () => {
    if (isSaving) return;
    if (!createData.employeeEmail) { alert('Sélectionnez un salarié.'); return; }
    if (!createData.startDate || !createData.startTime) { alert('Saisissez l\'heure de début.'); return; }
    if (!createData.endDate || !createData.endTime) { alert('Saisissez l\'heure de fin.'); return; }

    try {
      setIsSaving(true);

      const employee = employees.find(e => e.email === createData.employeeEmail);
      const startDateTime = new Date(`${createData.startDate}T${createData.startTime}`);
      const endDateTime   = new Date(`${createData.endDate}T${createData.endTime}`);

      if (endDateTime <= startDateTime) {
        alert('L\'heure de fin doit être après l\'heure de début.');
        return;
      }

      const sessionId = uuidv4();
      let totalPauseTime = 0;
      const newBreaks = [];

      // Créer les pauses
      for (const b of (createData.breaks || [])) {
        if (b.startTime && b.endTime) {
          const bStart = new Date(b.startTime);
          const bEnd   = new Date(b.endTime);
          const dur    = bEnd.getTime() - bStart.getTime();
          if (dur > 0) {
            totalPauseTime += dur;
            const breakRef = await addDoc(collection(db, 'breaks'), {
              sessionId,
              startTime:  Timestamp.fromDate(bStart),
              endTime:    Timestamp.fromDate(bEnd),
              duration:   dur,
              status:     'completed',
              createdAt:  Timestamp.fromDate(new Date()),
              adminCreated: true,
            });
            newBreaks.push({ id: breakRef.id, startTime: bStart, endTime: bEnd, duration: dur });
          }
        }
      }

      const totalSession = endDateTime.getTime() - startDateTime.getTime();
      const totalWork    = Math.max(0, totalSession - totalPauseTime);

      await setDoc(doc(db, 'workSessions', sessionId), {
        employeeEmail:  createData.employeeEmail,
        employeeName:   employee ? `${employee.prenom} ${employee.nom}` : createData.employeeEmail,
        siteDeTravail:  employee?.siteDeTravail || 'Non défini',
        startTime:      Timestamp.fromDate(startDateTime),
        endTime:        Timestamp.fromDate(endDateTime),
        status:         'completed',
        isOnBreak:      false,
        totalPauseTime,
        totalSessionTime: totalSession,
        totalWorkTime:  totalWork,
        breaks:         newBreaks,
        adminCreated:   true,
        adminCreatedAt: Timestamp.fromDate(new Date()),
        createdAt:      Timestamp.fromDate(startDateTime),
      });

      // Appliquer les règles de tolérance
      try {
        const { workSessionService } = await import('../services/workSessionService');
        await workSessionService.applyToleranceRules(sessionId);
      } catch (e) {
        console.warn('applyToleranceRules:', e.message);
      }

      await fetchWorkSessions();
      setShowCreateModal(false);
      setCreateData({});
      alert('Session créée avec succès !');

    } catch (error) {
      console.error('Erreur création session:', error);
      alert('Erreur : ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────

  // Charger la config admin (temps max)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configRef = doc(db, "adminConfigs", "global");
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
          const data = configSnap.data();
          if (data && data.maxSessionTimeHours) {
            setMaxSessionTime(Number(data.maxSessionTimeHours));
          }
        }
      } catch (e) {
        console.warn("Impossible de charger la config admin, utilisation de la valeur par défaut (12h)");
      }
    };
    fetchConfig();
  }, []);

  // Fonction utilitaire pour détecter une anomalie de temps
  const isSessionOverMax = (session) => {
    if (!session.startTime || !session.endTime) return false;
    const start = session.startTime.seconds ? session.startTime.seconds * 1000 : new Date(session.startTime).getTime();
    const end = session.endTime.seconds ? session.endTime.seconds * 1000 : new Date(session.endTime).getTime();
    const durationMs = end - start;
    return durationMs > maxSessionTime * 3600 * 1000;
  };

  if (loading) {
    return <div className="loading">Chargement en cours...</div>;
  }

  if (error) {
    return <div className="error">Erreur : {error}</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="logo-container">
        <img 
          src="/logo.png"
          alt="Logo" 
          style={{ 
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }} 
        />
      </div>
      <h1>Espace Administrateur</h1>
      

      <div className="admin-nav">
        <button className="admin-button" onClick={() => navigate("/admin/employees")}>Gestion des salariés</button>
        <button className="admin-button" onClick={() => navigate("/admin/sites")}>Gestion des zones de travail</button>
        <button className="admin-button" onClick={() => navigate("/admin/config")}>Configuration</button>
        <button 
          className="admin-button" 
          onClick={() => navigate("/admin/absences")}
          style={{position: 'relative'}}
        >
          Gestion des Absences
          {newAbsenceRequests > 0 && (
            <span style={{
              position: 'absolute', 
              top: 8, 
              right: 12, 
              minWidth: 16, 
              height: 16, 
              background: '#ff5722', 
              borderRadius: '50%', 
              border: '2px solid #fff', 
              boxShadow: '0 0 0 2px #ff5722', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#fff'
            }}>
              {newAbsenceRequests}
            </span>
          )}
        </button>
        <button
          className="admin-button"
          onClick={() => fetchWorkSessions(selectedMonth)}
          title="Recharger les sessions sans recharger la page"
          style={{ background: '#607d8b', color: '#fff' }}
        >
          🔄 Actualiser
        </button>
        <button className="admin-button" onClick={openCreateModal} style={{background:'#4CAF50', color:'#fff'}}>
          ➕ Session manuelle
        </button>
        <button className="admin-button" onClick={() => navigate("/admin/archives")}>Archives</button>
      </div>

      <div className="filters">
        <label>
          Filtrer par salarié :
          <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
            <option value="">Tous</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.email}>
                {emp.nom} {emp.prenom}
              </option>
            ))}
          </select>
        </label>

        <label>
          Mois :
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {months.map((month, idx) => {
              // Convertir YYYY-MM en format français lisible
              const [year, monthNum] = month.split('-');
              const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                                  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
              const monthLabel = `${monthNames[parseInt(monthNum) - 1]} ${year}`;
              
              return (
                <option key={idx} value={month}>{monthLabel}</option>
              );
            })}
          </select>
        </label>

        <div className="export-buttons">
          <button onClick={exportToCSV}>📥 Exporter en CSV</button>
          <button onClick={exportToPDF}>📄 Exporter en PDF</button>
        </div>

      </div>

      <div style={{ overflowX: 'auto', width: '100%', paddingBottom: showValidationBanner ? '120px' : '0' }}>
        <table className="session-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Nom</th>
              <th style={{ textAlign: 'center' }}>Date</th>
              <th style={{ textAlign: 'center' }}>Heure Début<br /><small>(Pondérée / Réelle)</small></th>
              <th style={{ textAlign: 'center' }}>Heure Fin<br /><small>(Pondérée / Réelle)</small></th>
              <th style={{ textAlign: 'center' }}>Temps Travail</th>
              <th style={{ textAlign: 'center' }}>Temps Pause</th>
              <th style={{ textAlign: 'center' }}>Sites autorisés</th>
              <th style={{ textAlign: 'center', width: '130px' }}>Zone Début</th>
              <th style={{ textAlign: 'center', width: '130px' }}>Zone Fin</th>
              <th style={{ textAlign: 'center', width: '150px' }}>Zone(s) Pause(s)</th>
              <th style={{ textAlign: 'center', width: '90px' }}>GPS Début</th>
              <th style={{ textAlign: 'center', width: '90px' }}>GPS Fin</th>
              <th style={{ textAlign: 'center', width: '80px' }}>📱 Appareil</th>
              <th style={{ width: 16, textAlign: 'center' }}>Info</th>
            </tr>
          </thead>
          <tbody>
            {pagedSessions.map((session) => (
              <tr key={session.id}>
                <td style={{ textAlign: 'center' }}>{getEmployeeDisplayName(session)}</td>
                <td style={{ textAlign: 'center' }}>{(() => {
                  const startTimestamp = session.startTime?.seconds;
                  if (!startTimestamp) return '';
                  const startDate = new Date(startTimestamp * 1000);
                  return startDate.toLocaleDateString('fr-FR');
                })()}</td>
                <td style={{ textAlign: 'center' }}>{getStartTimeDisplay(session)}</td>
                <td style={{ textAlign: 'center' }}>{getEndTimeDisplay(session)}</td>
                <td style={{ fontWeight: 'bold', textAlign: 'center' }}>{getWorkDuration(session)}</td>
                <td style={{ textAlign: 'center' }}>{getPauseDuration(session)}</td>
                <td style={{ textAlign: 'center' }}>{getEmployeeAuthorizedSites(session)}</td>
                <td style={{ textAlign: 'center' }}>{renderZoneCell(session.startLocation, employees.find(emp => emp.email === session.employeeEmail), sites, 'start')}</td>
                <td style={{ textAlign: 'center' }}>{renderZoneCell(session.endLocation, employees.find(emp => emp.email === session.employeeEmail), sites, 'end')}</td>
                <td style={{ textAlign: 'center' }}>{getBreakLocations(session)}</td>
                <td style={{ textAlign: 'center' }}>{(() => {
                  const gpsStart = session.startLocation;
                  return gpsStart ? getGPSLink(gpsStart) : <span className="no-location">Non disponible</span>;
                })()}</td>
                <td style={{ textAlign: 'center' }}>{(() => {
                  const gpsEnd = session.endLocation;
                  return gpsEnd ? getGPSLink(gpsEnd) : <span className="no-location">Non disponible</span>;
                })()}</td>
                <td style={{ 
                  textAlign: 'left', 
                  fontSize: '9px', 
                  maxWidth: '200px',
                  wordBreak: 'break-word',
                  padding: '4px 8px'
                }}>
                  {formatDeviceInfo(session.device)}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {isSessionOverMax(session) ? (
                    <span style={{ background: '#f44336', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Anomalie Temps de Session" onClick={() => handleEditSession(session)}></span>
                  ) : session.isEscalationValidated || session.originalEscalationId ? (
                    <span style={{ background: '#ff9800', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Session issue d'une escalade" onClick={() => handleEditSession(session)}></span>
                  ) : session.adminModified ? (
                    <span style={{ background: '#ffe066', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Session automatique modifiée manuellement" onClick={() => handleEditSession(session)}></span>
                  ) : (
                    <span style={{ background: '#2196f3', width: 16, height: 16, borderRadius: '50%', display: 'inline-block', cursor: 'pointer' }} title="Session automatique" onClick={() => handleEditSession(session)}></span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td colSpan="6" style={{ textAlign: "right" }}><strong>Total :</strong></td>
              <td><strong>{formatTime(pagedSessions.reduce((total, session) => {
                if (session.adjustedWorkTime != null) {
                  return total + session.adjustedWorkTime;
                }
                if (session.startTime?.seconds && session.endTime?.seconds) {
                  return total + Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0));
                }
                return total;
              }, 0))}</strong></td>
              <td><strong>{formatTime(pagedSessions.reduce((total, session) => total + (session.totalPauseTime || 0), 0))}</strong></td>
              <td colSpan="7">-</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Pagination style admin absences */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 16, gap: 16}}>
        <span>Éléments par page :</span>
        <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} style={{padding: '4px 8px', borderRadius: 6}}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span>{totalSessions === 0 ? '0' : `${page * rowsPerPage + 1} – ${Math.min((page + 1) * rowsPerPage, totalSessions)} sur ${totalSessions}`}</span>
        <button onClick={() => setPage(0)} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&#124;&lt;</button>
        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&lt;</button>
        <button onClick={() => setPage(p => Math.min(Math.ceil(totalSessions / rowsPerPage) - 1, p + 1))} disabled={page >= Math.ceil(totalSessions / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalSessions / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;</button>
        <button onClick={() => setPage(Math.max(0, Math.ceil(totalSessions / rowsPerPage) - 1))} disabled={page >= Math.ceil(totalSessions / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalSessions / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;&#124;</button>
      </div>

      {/* ── Modal création session manuelle ── */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{maxWidth: 560}}>
            <div className="modal-header">
              <h2>➕ Créer une session manuelle</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{display:'flex', flexDirection:'column', gap:14}}>

              {/* Salarié */}
              <div>
                <label style={{fontWeight:600}}>Salarié *</label>
                <select
                  className="form-control"
                  value={createData.employeeEmail}
                  onChange={e => setCreateData(p => ({...p, employeeEmail: e.target.value}))}
                >
                  <option value="">-- Sélectionner --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.email}>
                      {emp.prenom} {emp.nom} ({emp.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Début */}
              <div style={{display:'flex', gap:10}}>
                <div style={{flex:1}}>
                  <label style={{fontWeight:600}}>Date début *</label>
                  <input type="date" className="form-control"
                    value={createData.startDate}
                    onChange={e => setCreateData(p => ({...p, startDate: e.target.value, endDate: e.target.value}))}
                  />
                </div>
                <div style={{flex:1}}>
                  <label style={{fontWeight:600}}>Heure début *</label>
                  <input type="time" className="form-control"
                    value={createData.startTime}
                    onChange={e => setCreateData(p => ({...p, startTime: e.target.value}))}
                  />
                </div>
              </div>

              {/* Fin */}
              <div style={{display:'flex', gap:10}}>
                <div style={{flex:1}}>
                  <label style={{fontWeight:600}}>Date fin *</label>
                  <input type="date" className="form-control"
                    value={createData.endDate}
                    onChange={e => setCreateData(p => ({...p, endDate: e.target.value}))}
                  />
                </div>
                <div style={{flex:1}}>
                  <label style={{fontWeight:600}}>Heure fin *</label>
                  <input type="time" className="form-control"
                    value={createData.endTime}
                    onChange={e => setCreateData(p => ({...p, endTime: e.target.value}))}
                  />
                </div>
              </div>

              {/* Pauses */}
              <div>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6}}>
                  <label style={{fontWeight:600}}>Pauses</label>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setCreateData(p => ({
                      ...p,
                      breaks: [...(p.breaks||[]), {
                        startTime: `${p.startDate}T12:00`,
                        endTime:   `${p.startDate}T13:00`
                      }]
                    }))}
                  >+ Ajouter une pause</button>
                </div>
                {(createData.breaks || []).map((b, i) => (
                  <div key={i} style={{display:'flex', gap:8, alignItems:'center', marginBottom:6}}>
                    <input type="datetime-local" className="form-control"
                      value={b.startTime}
                      onChange={e => {
                        const breaks = [...createData.breaks];
                        breaks[i] = {...breaks[i], startTime: e.target.value};
                        setCreateData(p => ({...p, breaks}));
                      }}
                    />
                    <span>→</span>
                    <input type="datetime-local" className="form-control"
                      value={b.endTime}
                      onChange={e => {
                        const breaks = [...createData.breaks];
                        breaks[i] = {...breaks[i], endTime: e.target.value};
                        setCreateData(p => ({...p, breaks}));
                      }}
                    />
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setCreateData(p => ({
                        ...p,
                        breaks: p.breaks.filter((_, j) => j !== i)
                      }))}
                    >✕</button>
                  </div>
                ))}
              </div>

            </div>
            <div className="modal-footer" style={{display:'flex', gap:10, justifyContent:'flex-end', padding:16}}>
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Annuler</button>
              <button
                className="btn btn-success"
                onClick={handleSaveCreate}
                disabled={isSaving}
              >
                {isSaving ? '⏳ Création...' : '✅ Créer la session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Modifier la session</h3>
              <button onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            
            <div className="modal-body">
              {/* Section Début de session */}
              <div className="modal-section">
                <h4>🕐 Début de session</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Date de début :</label>
                    <input 
                      type="date" 
                      value={editData.startDate} 
                      onChange={e => setEditData({...editData, startDate: e.target.value})} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Heure de début :</label>
                    <input 
                      type="time" 
                      value={editData.startTime} 
                      onChange={e => setEditData({...editData, startTime: e.target.value})} 
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Site de début :</label>
                  <select 
                    value={String(editData.startSite || '')} 
                    onChange={e => setEditData({...editData, startSite: e.target.value})}
                  >
                    <option value="">Sélectionner un site</option>
                    <option value="HORS_ZONE">HORS ZONE</option>
                    {sites.map(site => (
                      <option key={site.id} value={String(site.id)}>{site.nom}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Section Fin de session */}
              <div className="modal-section">
                <h4>🕐 Fin de session</h4>
                {!editSession?.endTime && (
                  <div style={{ background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
                    ⚠️ Session sans heure de fin. Si le salarié a fini <strong>après minuit</strong> (ex. 00h30), changez la <strong>date de fin au lendemain</strong>.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>Date de fin :</label>
                    <input
                      type="date"
                      value={editData.endDate}
                      onChange={e => setEditData({...editData, endDate: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Heure de fin :</label>
                    <input
                      type="time"
                      value={editData.endTime}
                      onChange={e => setEditData({...editData, endTime: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Site de fin :</label>
                  <select 
                    value={String(editData.endSite || '')} 
                    onChange={e => setEditData({...editData, endSite: e.target.value})}
                  >
                    <option value="">Sélectionner un site</option>
                    <option value="HORS_ZONE">HORS ZONE</option>
                    {sites.map(site => (
                      <option key={site.id} value={String(site.id)}>{site.nom}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Section Pauses */}
              <div className="modal-section">
                <h4>☕ Pauses</h4>
                
                {(editData.breaks && editData.breaks.length > 0 ? editData.breaks : []).map((b, idx, arr) => {
                  const startSiteId = b.startLocation?.siteId || '';
                  const endSiteId = b.endLocation?.siteId || '';
                  
                  return (
                    <div key={idx} className="break-item">
                      <div className="break-header">
                        <div className="break-title">
                          Pause n°{idx + 1}
                        </div>
                        {arr.length > 1 && (
                          <button 
                            className="break-delete-btn"
                            onClick={() => {
                              const newBreaks = editData.breaks.filter((_, i) => i !== idx);
                              setEditData({...editData, breaks: newBreaks});
                            }}
                            title="Supprimer cette pause"
                          >
                            🗑️ Supprimer
                          </button>
                        )}
                      </div>
                      
                      <div className="break-grid">
                        {/* Début de pause */}
                        <div className="break-section">
                          <label style={{ color: '#2196f3' }}>Début de pause :</label>
                          <input 
                            type="datetime-local" 
                            value={b.startTime} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].startTime = e.target.value;
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          />
                          <label style={{ marginTop: '8px' }}>Site début :</label>
                          <select 
                            value={startSiteId} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].startLocation = {
                                ...(newBreaks[idx].startLocation || {}), 
                                siteId: e.target.value
                              };
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          >
                            <option value="">Sélectionner un site</option>
                            <option value="HORS_ZONE">HORS ZONE</option>
                            {sites.map(site => (
                              <option key={site.id} value={String(site.id)}>{site.nom}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Fin de pause */}
                        <div className="break-section">
                          <label style={{ color: '#f44336' }}>Fin de pause :</label>
                          <input 
                            type="datetime-local" 
                            value={b.endTime} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].endTime = e.target.value;
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          />
                          <label style={{ marginTop: '8px' }}>Site fin :</label>
                          <select 
                            value={endSiteId} 
                            onChange={e => {
                              const newBreaks = [...editData.breaks];
                              newBreaks[idx].endLocation = {
                                ...(newBreaks[idx].endLocation || {}), 
                                siteId: e.target.value
                              };
                              setEditData({...editData, breaks: newBreaks});
                            }}
                          >
                            <option value="">Sélectionner un site</option>
                            <option value="HORS_ZONE">HORS ZONE</option>
                            {sites.map(site => (
                              <option key={site.id} value={String(site.id)}>{site.nom}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                <button 
                  className="add-break-btn"
                  onClick={() => setEditData({
                    ...editData, 
                    breaks: [...(editData.breaks || []), {
                      startTime: '',
                      endTime: '',
                      startLocation: { siteId: '' },
                      endLocation: { siteId: '' }
                    }]
                  })}
                  title="Ajouter une nouvelle pause"
                >
                  ➕ Ajouter une pause
                </button>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-save" 
                onClick={handleSaveEdit}
                disabled={isSaving}
                style={{ 
                  background: isSaving ? '#ccc' : '#4caf50', 
                  color: 'white',
                  cursor: isSaving ? 'not-allowed' : 'pointer'
                }}
              >
                {isSaving ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </button>
              <button 
                className="btn-cancel" 
                onClick={() => setShowEditModal(false)}
                disabled={isSaving}
                style={{ 
                  background: isSaving ? '#ccc' : '#f44336', 
                  color: 'white',
                  cursor: isSaving ? 'not-allowed' : 'pointer'
                }}
              >
                ❌ Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bandeau de validation de feuille de temps */}
      {showValidationBanner && timesheetData && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '10px 20px',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
          minHeight: '56px'
        }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
              📋 {timesheetData.employeeName}
            </div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>
              {timesheetData.monthName} {timesheetData.year} • {timesheetData.sessionCount} sessions • {Math.round(timesheetData.totalWorkTime / 3600000)}h
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/admin/timesheet-validation')}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              ← Retour
            </button>
            
            <button
              onClick={() => setShowCommentModal(true)}
              style={{
                background: '#ffc107',
                color: '#000',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              📝 Notes
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('Refuser cette feuille de temps ?')) return;
                const reason = window.prompt('Raison du refus :');
                if (!reason) return;
                
                try {
                  await updateDoc(doc(db, 'workSessionsMonth', timesheetData.id), {
                    status: 'Refusé',
                    rejectionReason: reason,
                    adminValidatedAt: new Date(),
                    updatedAt: new Date()
                  });
                  alert('✅ Feuille refusée');
                  navigate('/admin/timesheet-validation');
                } catch (error) {
                  console.error('❌ Erreur:', error);
                  alert('Erreur lors du refus');
                }
              }}
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              ✗ Refuser
            </button>
            
            <button
              onClick={async () => {
                if (!window.confirm('Valider cette feuille de temps ? L\'employé pourra la signer.')) return;
                
                try {
                  await updateDoc(doc(db, 'workSessionsMonth', timesheetData.id), {
                    status: 'En attente de signature salarié',
                    adminValidatedAt: new Date(),
                    updatedAt: new Date(),
                    employerComment: employerComment,
                    timesheetNotificationCreated: true,
                    timesheetNotificationVu: false
                  });
                  alert('✅ Feuille validée');
                  navigate('/admin/timesheet-validation');
                } catch (error) {
                  console.error('❌ Erreur:', error);
                  alert('Erreur lors de la validation');
                }
              }}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: '36px',
                whiteSpace: 'nowrap'
              }}
            >
              ✓ Valider
            </button>
          </div>
        </div>
      )}

      {/* Modal de commentaires */}
      {showCommentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: 32,
            borderRadius: 12,
            maxWidth: 500,
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>💬 Notes et commentaires employeur</h3>
            <textarea
              value={employerComment}
              onChange={(e) => setEmployerComment(e.target.value)}
              placeholder="Ajoutez des notes ou commentaires concernant cette feuille de temps..."
              style={{
                width: '100%',
                minHeight: 120,
                padding: 12,
                borderRadius: 6,
                border: '1px solid #ddd',
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCommentModal(false)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  try {
                    await updateDoc(doc(db, 'workSessionsMonth', timesheetData.id), {
                      employerComment: employerComment,
                      updatedAt: new Date()
                    });
                    alert('✅ Commentaire sauvegardé');
                    setShowCommentModal(false);
                  } catch (error) {
                    console.error('❌ Erreur:', error);
                    alert('Erreur lors de la sauvegarde');
                  }
                }}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                💾 Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;